import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
} from 'react-query';
import { queryCreate, queryGet, queryUpdate } from './fetch';

export enum FieldType {
  SINGLE_CHOICE = 'single_choice',
  MULTIPLE_CHOICE = 'multiple_choice',
  SHORT_TEXT = 'short_text',
  LONG_TEXT = 'long_text',
  INFORMATION_PAGE = 'information_page',
}

export interface AbstractField {
  id: string;
  title: string;
  subTitle?: string;
  ref: string;
  type: FieldType;
  validation?: {
    required?: boolean;
  };
  jumpActions?: FieldJump[];
}

export interface FieldChoice {
  ref: string;
  label: string;
  icon?: string;
  value?: { min: number | null; max: number | null };
  quota?: number | null;
  metadata?: { tags: string[] };
}

export interface SelectChoiceField extends AbstractField {
  type: FieldType.SINGLE_CHOICE | FieldType.MULTIPLE_CHOICE;
  properties: {
    randomize: boolean;
    choices?: FieldChoice[];
    autocomplete?: boolean;
  };
  validation?: {
    required?: boolean;
  };
}

export interface SingleChoiceField extends SelectChoiceField {
  type: FieldType.SINGLE_CHOICE;
}

export enum CustomSelectChoice {
  Other = 'other',
  None = 'none',
}

export interface MultipleChoiceField extends SelectChoiceField {
  type: FieldType.MULTIPLE_CHOICE;
  validation?: {
    required?: boolean;
    minSelection?: number | null;
    maxSelection?: number | null;
  };
}

export interface ShortTextField extends AbstractField {
  type: FieldType.SHORT_TEXT;
}

export interface LongTextField extends AbstractField {
  type: FieldType.LONG_TEXT;
}

export interface InformationPageField extends AbstractField {
  type: FieldType.INFORMATION_PAGE;
  properties: {
    description?: string;
  };
}
export type Field =
  | SingleChoiceField
  | MultipleChoiceField
  | ShortTextField
  | LongTextField
  | InformationPageField;

export enum LogicJumpActionType {
  JUMP = 'JUMP',
  END_FORM = 'END_FORM',
  NEXT_QUESTION = 'NEXT_QUESTION',
}

export interface FieldJump {
  id: string;
  fieldId: string;
  type: LogicJumpActionType;
  toFieldId?: string;
  conditionChoiceRefs?: string[];
  updatedAt?: Date;
  createdAt?: Date;
}

export interface Form {
  id: string;
  fieldIds: string[];
  fields?: Array<Field>;
  organizationId?: string;
  // logicJumps?: Array<LogicJump>;
}

export const useForm = (id?: string) => {
  return useQuery<Form>(['forms', id], queryGet(`/v2/forms/${id}`), {
    enabled: !!id,
  });
};

export const useCreateForm = (
  organizationId?: string,
  options?: UseMutationOptions<Form, any, Partial<Form>>,
) => {
  return useMutation<Form, any, Partial<Form>>((form: Partial<Form>) => {
    return queryCreate('/v2/forms')({
      ...form,
      organizationId: form.organizationId
        ? form.organizationId
        : organizationId,
    });
  }, options);
};

export const useUpdateForm = (
  id?: string,
  options?: UseMutationOptions<Form, any, Partial<Form>>,
) => {
  const queryClient = useQueryClient();
  return useMutation<Form, any, Partial<Form>>(queryUpdate(`/v2/forms/${id}`), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(['forms', id], data);

      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
  });
};

export const isSelectChoiceField = (
  field: AbstractField,
): field is SelectChoiceField => {
  return (
    field.type === FieldType.SINGLE_CHOICE ||
    field.type === FieldType.MULTIPLE_CHOICE
  );
};
